import React, { useState, useEffect, useContext, useMemo } from "react";
import SecurityScore from "./SecurityScore";
import Compliance from "./Compliance";
import AssetInsightCard from "./AssetInsight";
import CostInsight from "./CostInsight";
import EventInsight from "./EventInsight"; // Importing the EventInsight component
import SummaryCard from "./SummaryCard";
import AccountsCard from "./AccountsCard";
import { ResponsiveGridLayout } from "./components/ResponsiveGridLayout";
import InaccessibleResources from "../../helpers/InaccessibleResources";
import loadingGif from "./../../assets/images/aws/loadingGif.gif";
import { getLicensingData } from "../../store/actions";
import { connect } from "react-redux";
import jwt_decode from "jwt-decode";
import { useAccounts } from "../../components/Common/HelperComponents/GetDataFromStore";
import CostOptimization from "./CostOptimization";
import IaCInsight from "./IaCInsight";
import WAFRInsight from "./WAFRInsight";
import { Box, Typography } from "@mui/material";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import {
  CloudProviderContext,
  shouldShowAzurePlaceholderView,
} from "../../CloudProviderContext";

const DashboardPage = (props) => {
  const history = useHistory();
  const accounts = useAccounts();
  const { selectedProvider } = useContext(CloudProviderContext);
  const [isAccountConfigured, setIsAccountConfigured] = useState(null);
  const [accountStatus, setAccountStatus] = useState(null);

  // const activeUserAccounts = useMemo(() => {
  //   let userInfo;
  //   if (localStorage.getItem("authUser")) {
  //     const obj = JSON.parse(localStorage.getItem("authUser"));
  //     userInfo = jwt_decode(obj.idToken).email;
  //   }

  //   if (!accounts || !userInfo) {
  //     return [];
  //   }

  //   return accounts.filter(
  //     (account) =>
  //       account.status === "ACTIVE" &&
  //       account.listusr?.some((user) => user.userName === userInfo)
  //   );
  // }, [accounts]);

  const showAzurePlaceholder = useMemo(
    () => shouldShowAzurePlaceholderView(selectedProvider),
    [selectedProvider]
  );

  // Breakpoints for different screen sizes
  const breakpoints = { lg: 1600, md: 1200, sm: 768, xs: 480, xxs: 0 };
  const cols = { lg: 16, md: 12, sm: 6, xs: 4, xxs: 2 };

  // Extra row height + in-card scroll (see SummaryCard) so zoom / text scaling doesn’t clip KPI values.
  const layouts = {
    lg: [
      { i: "accountsCard", x: 0, y: 0, w: 2, h: 1.5, static: true },
      { i: "summaryCard", x: 2, y: 0, w: 14, h: 1.5 },
      { i: "assetInsight", x: 0, y: 1.5, w: 4, h: 6 },
      { i: "costInsight", x: 12, y: 1.5, w: 4, h: 1.5 },
      { i: "costOptimization", x: 12, y: 3, w: 4, h: 1.5 },
      { i: "eventInsight", x: 4, y: 7.5, w: 4, h: 3 },
      { i: "iacInsight", x: 8, y: 7.5, w: 4, h: 3 },
      { i: "wafrInsight", x: 12, y: 7.5, w: 4, h: 3 },
      { i: "securityScore", x: 4, y: 1.5, w: 4, h: 3 },
      { i: "compliance", x: 8, y: 1.5, w: 4, h: 3 },
    ],
    md: [
      { i: "accountsCard", x: 0, y: 0, w: 2, h: 1.5, static: true },
      { i: "summaryCard", x: 2, y: 0, w: 10, h: 1.5 },
      { i: "assetInsight", x: 0, y: 1.5, w: 3, h: 6 },
      { i: "costInsight", x: 9, y: 1.5, w: 3, h: 1.7 },
      { i: "costOptimization", x: 9, y: 3.2, w: 3, h: 1.3 },
      { i: "eventInsight", x: 3, y: 7.5, w: 3, h: 3 },
      { i: "iacInsight", x: 6, y: 7.5, w: 3, h: 3 },
      { i: "wafrInsight", x: 9, y: 7.5, w: 3, h: 3 },
      { i: "securityScore", x: 3, y: 1.5, w: 3, h: 3 },
      { i: "compliance", x: 6, y: 1.5, w: 3, h: 3 },
    ],
    sm: [
      { i: "accountsCard", x: 0, y: 0, w: 2, h: 1.5, static: true },
      { i: "summaryCard", x: 2, y: 0, w: 4, h: 1.5 },
      { i: "assetInsight", x: 0, y: 1.5, w: 6, h: 6 },
      { i: "costInsight", x: 0, y: 7.5, w: 6, h: 1.5 },
      { i: "costOptimization", x: 0, y: 9, w: 6, h: 1.5 },
      { i: "eventInsight", x: 0, y: 10.5, w: 3, h: 3 },
      { i: "iacInsight", x: 3, y: 10.5, w: 3, h: 3 },
      { i: "wafrInsight", x: 0, y: 13.5, w: 3, h: 3 },
      { i: "securityScore", x: 0, y: 16.5, w: 6, h: 3 },
      { i: "compliance", x: 0, y: 19.5, w: 6, h: 3 },
    ],
    xs: [
      { i: "accountsCard", x: 0, y: 0, w: 4, h: 1.5, static: true },
      { i: "summaryCard", x: 0, y: 1.5, w: 4, h: 1.5 },
      { i: "assetInsight", x: 0, y: 3, w: 4, h: 6 },
      { i: "costInsight", x: 0, y: 9, w: 4, h: 1.5 },
      { i: "costOptimization", x: 0, y: 10.5, w: 4, h: 1.5 },
      { i: "eventInsight", x: 0, y: 12, w: 4, h: 3 },
      { i: "iacInsight", x: 0, y: 15, w: 4, h: 3 },
      { i: "wafrInsight", x: 0, y: 18, w: 4, h: 3 },
      { i: "securityScore", x: 0, y: 21, w: 4, h: 3 },
      { i: "compliance", x: 0, y: 24, w: 4, h: 3 },
    ],
  };

  // Azure: Accounts + Security Score + Compliance (Accounts replaces ARN spacer)
  const azureLayouts = {
    lg: [
      { i: "accountsCard", x: 0, y: 0, w: 2, h: 1.5, static: true },
      { i: "securityScore", x: 4, y: 1.5, w: 4, h: 3 },
      { i: "compliance", x: 8, y: 1.5, w: 4, h: 3 },
    ],
    md: [
      { i: "accountsCard", x: 0, y: 0, w: 2, h: 1.5, static: true },
      { i: "securityScore", x: 3, y: 1.5, w: 3, h: 3 },
      { i: "compliance", x: 6, y: 1.5, w: 3, h: 3 },
    ],
    sm: [
      { i: "accountsCard", x: 0, y: 0, w: 2, h: 1.5, static: true },
      { i: "securityScore", x: 0, y: 1.5, w: 6, h: 3 },
      { i: "compliance", x: 0, y: 4.5, w: 6, h: 3 },
    ],
    xs: [
      { i: "accountsCard", x: 0, y: 0, w: 4, h: 1.5, static: true },
      { i: "securityScore", x: 0, y: 1.5, w: 4, h: 3 },
      { i: "compliance", x: 0, y: 4.5, w: 4, h: 3 },
    ],
  };

  let awsAccountConfigEl = null;

  // console.log("~~~~~props.licenseData : ", props.licenseData);
  // console.log("~~~~~isAccountConfigured : ", isAccountConfigured);
  // console.log("~~~~~accountStatus : ", accountStatus);
  const getLicensingData = props.getLicensingData;
  const currentLicense = props.licenseData?.data?.licenseName;
  const currentLicenseLoading = props.licenseData?.loading;
  useEffect(() => {
    if (!currentLicense && !currentLicenseLoading) {
      getLicensingData();
    }
  }, [currentLicense, getLicensingData, currentLicenseLoading]);

  useEffect(() => {
    let accCodes;
    let accountNumbers = [];
    let userInfo;
    if (localStorage.getItem("authUser")) {
      const obj = JSON.parse(localStorage.getItem("authUser"));
      userInfo = jwt_decode(obj.idToken).email;
    }

    if (accounts) {
      accCodes = accounts
        .filter(
          (account) =>
            account.status === "ACTIVE" &&
            account.listusr?.some((user) => user.userName === userInfo)
        )
        .map((account) => account.accountCode);
      accounts.map((account) => accountNumbers.push(account.accountNumber));
      // console.log("accCodes: ", accCodes);
      if (accCodes.length > 0) {
        if (isAccountConfigured === null) {
          setIsAccountConfigured(true);
          setAccountStatus("ACTIVE");
        }
      } else if (isAccountConfigured === null) {
        setIsAccountConfigured(false);
        const inactiveAccounts = accounts.filter(
          (account) =>
            account.status === "INACTIVE" && account.number !== "INIT00000001"
        );
        if (inactiveAccounts.length > 0) {
          setAccountStatus("INACTIVE");
        } else {
          setAccountStatus("NOACCOUNT");
        }
      }
    }
  }, [accounts, isAccountConfigured]);

  // Generating spinner UI while license data is loading.
  if (currentLicenseLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          height: "100vh",
          alignItems: "center",
        }}
      >
        <img src={loadingGif} height={40} alt="Loading Spinner" />
      </div>
    );
  }

  if (props.licenseData?.data?.createdDate !== null) {
    const createdDate = props.licenseData?.data?.createdDate;
    const createdTime =
      new Date(createdDate).getTime() -
      new Date(createdDate).getTimezoneOffset() * 60 * 1000;
    const currentTime = new Date().getTime();
    const oneHourInMillis = 60 * 60 * 1000;
    if (currentTime - createdTime <= oneHourInMillis) {
      return (
        <div className="page-content" style={{ background: "#EAEAEA" }}>
          <InaccessibleResources message="Please check back in an hour as Cloudcatcher is scanning your environment!" />
        </div>
      );
    }
  }
  // Render account status message
  const renderAccountMessage = () => {
    if (isAccountConfigured === null || isAccountConfigured) return null;

    const messageContent =
      accountStatus === "INACTIVE" ? (
        <>
          All the AWS accounts in this organization are inactive. Please add a
          new AWS account or activate the inactive accounts through the{" "}
          <Typography
            component="span"
            onClick={() => history.push("/configuration/accounts")}
            sx={{
              color: "primary.main",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            configuration
          </Typography>{" "}
          menu on the left.
        </>
      ) : (
        <>
          This organization does not have any AWS account on-boarded. Please add
          an AWS account through{" "}
          <Typography
            component="span"
            onClick={() => history.push("/configuration/accounts")}
            sx={{
              color: "primary.main",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            configuration
          </Typography>{" "}
          menu on the left.
        </>
      );

    return (
      <Box
        className="page-content"
        sx={{
          minHeight: "80vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "grey.100",
          p: 3,
          mb: 5,
        }}
      >
        <Box
          sx={{
            maxWidth: "600px",
            width: "100%",
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 1,
            p: 4,
            textAlign: "center",
          }}
        >
          <Typography
            variant="h6"
            component="div"
            sx={{
              color: "text.primary",
              mb: 2,
              fontWeight: 500,
            }}
          >
            Account Configuration Required
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              lineHeight: 1.6,
            }}
          >
            {messageContent}
          </Typography>
        </Box>
      </Box>
    );
  };

  // Early return if account is not configured
  if (!isAccountConfigured) {
    return renderAccountMessage();
  }

  if (showAzurePlaceholder) {
    return (
      <ResponsiveGridLayout
        className="layout"
        breakpoints={breakpoints}
        cols={cols}
        layouts={azureLayouts}
        rowHeight={100}
        isDraggable={true}
        isResizable={false}
        draggableHandle=".drag-handle"
        compactType={null}
      >
        <div key="accountsCard">
          <AccountsCard />
        </div>
        <div key="securityScore">
          <SecurityScore key="security-AZURE" accountTypeFilter="AZURE" />
        </div>
        <div key="compliance">
          <Compliance key="compliance-AZURE" accountTypeFilter="AZURE" />
        </div>
      </ResponsiveGridLayout>
    );
  }

  if (currentLicense && accountStatus === "ACTIVE") {
    // Display Dashboard when everything is okay
    return (
      <ResponsiveGridLayout
        className="layout"
        breakpoints={breakpoints}
        cols={cols}
        layouts={layouts}
        rowHeight={100}
        isDraggable={true}
        isResizable={false}
        draggableHandle=".drag-handle"
      >
        <div key="accountsCard">
          <AccountsCard />
        </div>
        <div key="summaryCard">
          <SummaryCard />
        </div>
        <div key="costInsight">
          <CostInsight />
        </div>
        <div key="costOptimization">
          <CostOptimization />
        </div>
        <div key="eventInsight">
          {" "}
          {/* Added EventInsight */}
          <EventInsight />
        </div>
        <div key="securityScore">
          <SecurityScore key="security-AWS" accountTypeFilter="AWS" />
        </div>
        <div key="compliance">
          <Compliance key="compliance-AWS" accountTypeFilter="AWS" />
        </div>
        <div key="assetInsight">
          <AssetInsightCard />
        </div>
        <div key="iacInsight">
          <IaCInsight />
        </div>
        <div key="wafrInsight">
          <WAFRInsight />
        </div>
      </ResponsiveGridLayout>
    );
  }
};

const mapStateToProps = (state) => {
  return {
    licenseData: state.licensing,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getLicensingData: () => {
      dispatch(getLicensingData());
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(DashboardPage);
